/**
 * @author czy
 * 功能：结算列表
 */

import { Service } from '@/tools'
import type TCommon from '@/types/TCommon'

export class OrderService<T extends TSettle.ApiOrderPage> extends Service {
  constructor(path: string) {
    super()
    this.setPathPrefix(path)
  }

  private post2<P extends TCommon.ApiVersion = 'v1'>(url: `${P}/${keyof TSettle.ApiOrderPage}` | string, data?: any, toast?: string) {
    return this.post(url, data, toast)
  }

  // 查询所有结算科目
  // /api/settle/order/v1/getOrderPage
  getOrderPage = <K extends T['ApiGetOrderPage']>(data: K['req']): Promise<K['res']> => this.post2('v1/ApiGetOrderPage', data)

  //进项结算单创建 /api/settle/order/v1/saveSettleOrder
  saveSettleOrder = <K extends T['ApiSaveSettleOrder']>(data: K['req']): Promise<K['res']> => this.post2('v1/ApiSaveSettleOrder', data)

  // 查询结算单详情 /api/settle/order/v1/getSettleOrderById
  getSettleOrderById = <K extends T['ApiGetSettleOrderById']>(data: K['req']): Promise<K['res']> => this.post2('v1/ApiGetSettleOrderById', data)

  //  根据单个结算单号获取结算单关联商品信息（发票新增-详情调用）
  getSettleOrderItemsBySettleNo = <K extends T['ApiGetSettleOrderItemsBySettleNo']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSettleOrderItemsBySettleNo', data)

  // 根据多个结算单号获取结算单信息（发票详情调用）getSettleOrderListByNos
  getSettleOrderListByNos = <K extends T['ApiGetSettleOrderListByNos']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSettleOrderListByNos', data)

  // 销售结算目录-结算统计
  // /api/settle/order/v1/getSalesCatalogSettlementCount
  getSalesCatalogSettlementCount = <K extends T['ApiGetSalesCatalogSettlementCount']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogSettlementCount', data)

  // 销售结算目录-结算主体结算信息
  // /api/settle/order/v1/getSalesCatalogSettlementEntityInfo
  getSalesCatalogSettlementEntityInfo = <K extends T['ApiGetSalesCatalogSettlementEntityInfo']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogSettlementEntityInfo', data)

  // 销售结算目录-结算主体客户待结信息
  // /api/settle/order/v1/getSalesCatalogCustomerSettlementInfo
  getSalesCatalogCustomerSettlementInfo = <K extends T['ApiGetSalesCatalogCustomerSettlementInfo']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogCustomerSettlementInfo', data)

  // 销售结算目录-结算主体客户信息
  // /api/settle/order/v1/getSalesCatalogBusinessDocumentPage
  getSalesCatalogBusinessDocumentPage = <K extends T['ApiGetSalesCatalogBusinessDocumentPage']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogBusinessDocumentPage', data)

  // 销售结算目录-查询全选全部筛选后客户选中待结金额
  // /api/settle/order/v1/getCustomerCheckOutstandingAmount
  getCustomerCheckOutstandingAmount = <K extends T['ApiGetCustomerCheckOutstandingAmount']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetCustomerCheckOutstandingAmount', data)

  // 销售结算目录-确认结算单
  // /api/settle/order/v1/affirm
  affirm = <K extends T['ApiAffirm']>(data: K['req']): Promise<K['res']> => this.post2('v1/ApiAffirm', data)

  // 销售结算目录-返利结算单
  // /api/settle/order/v1/getSalesCatalogRebateSettlement
  getSalesCatalogRebateSettlement = <K extends T['ApiGetSalesCatalogRebateSettlement']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogRebateSettlement', data)

  // 销售结算目录-返利结算单状态修改
  // /api/settle/order/v1/updateRebateSettlementStatus
  updateRebateSettlementStatus = <K extends T['ApiUpdateRebateSettlementStatus']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiUpdateRebateSettlementStatus', data)

  // 销售结算目录-删除结算单
  // /api/settle/order/v1/deleteSettleOrder
  deleteSettleOrder = <K extends T['ApiDeleteSettleOrder']>(data: K['req']): Promise<K['res']> => this.post2('v1/ApiDeleteSettleOrder', data)

  // 根据结算主体查询下属客户信息
  // /api/settle/order/v1/getSettlementEntityCustomerInfo
  getSettlementEntityCustomerInfo = <K extends T['ApiGetSettlementEntityCustomerInfo']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSettlementEntityCustomerInfo', data)

  // 销售结算目录-查询预收抵扣信息
  // /api/settle/order/v1/getPaymentReceiptAdvanceSettleJoin
  getPaymentReceiptAdvanceSettleJoin = <K extends T['ApiGetPaymentReceiptAdvanceSettleJoin']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetPaymentReceiptAdvanceSettleJoin', data)

  // 销售结算目录-新增预收抵扣
  // /api/settle/order/v1/savePaymentReceiptAdvanceSettle
  savePaymentReceiptAdvanceSettle = <K extends T['ApiSavePaymentReceiptAdvanceSettle']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiSavePaymentReceiptAdvanceSettle', data)

  // 根据销项目录ID查询销项结算单列表
  // /api/settle/order/v1/getSalesSettleOrderList
  getSalesSettleOrderList = <K extends T['ApiGetSalesSettleOrderList']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesSettleOrderList', data)

  // 销售结算目录-查询业务单据待结明细
  // /api/settle/order/v1/getSalesCatalogBusinessDocumentDetailsPage
  getSalesCatalogBusinessDocumentDetailsPage = <K extends T['ApiGetSalesCatalogBusinessDocumentDetailsPage']>(data: K['req']): Promise<K['res']> =>
    this.post2('v1/ApiGetSalesCatalogBusinessDocumentDetailsPage', data)
}
